export const name = 'Language';

import { i18n, i18nSettings } from '@/i18n/settings'
import { useMainStore } from '@/stores/main'
import { Config } from '@/Config'
import * as Server from '@/services/Server'

/**
 * Language handling: one single place that keeps vue-i18n and the store (persisted in
 * localStorage) in sync, plus the user preference saved on the server.
 *
 * - guests: the choice lives on the client only (localStorage);
 * - registered users: user_settings.language is the source of truth. It is applied at
 *   login and saved from the settings page.
 */

/** Languages offered by the site (see Config.languages). */
export function supportedLanguages()
{
  return Object.keys(Config.languages)
}

/** Language used when neither the browser nor the user asks for a supported one. */
export function defaultLanguage()
{
  return i18nSettings.fallbackLocale
}

/**
 * Language the address carries (/it/eventi), or null when it carries none: every page is written with
 * its language in the address (see meta.php), so an address without one is moved to the visitor's own as
 * soon as the router looks at it (see settleAddress in router/index.js).
 */
export function fromAddress()
{
  return languageOfPath(window.location.pathname)
}

/**
 * Language written at the beginning of an address, or null when it carries none. A guard reads it from
 * the address it is going to, not from the browser's: the browser's still shows the previous one while a
 * navigation is being decided, so asking it there answers the same thing for ever.
 */
export function languageOfPath(path)
{
  const first = path.split('/')[1]

  return isSupported(first) ? first : null
}

/** Language the address is written in: the prefix when it has one, the default one otherwise. */
export function languageOfAddress()
{
  return fromAddress() ?? defaultLanguage()
}

export function isSupported(language)
{
  return supportedLanguages().includes(language)
}

/**
 * Language of the page: the one its address carries, or the default one when it carries none. Nothing
 * else takes a visitor somewhere else — neither the language remembered on this device nor the one the
 * browser asks for: they only decide where an address with no language of its own is moved to at boot
 * (see `start` and settleAddress in router/index.js).
 */
export function current()
{
  return languageOfAddress()
}

// Language this device asks for: the one chosen on it, then the browser one, then the default. It is read
// once at boot (`start`), because applying a language replaces the choice on the device with the language
// of the address: after that the store no longer says what the device asks for.
let device = null

/**
 * Language the app is running in: the one applied on the client, and therefore the one the router has
 * built its addresses for. It is the one to ask when the language of an address has to be written.
 */
export function applied()
{
  return i18n.global.locale.value
}

function askDevice()
{
  const store = useMainStore()
  const chosen = store.currentLanguage || (navigator.language || '').split('-')[0]

  return isSupported(chosen) ? chosen : defaultLanguage()
}

let onChange = () => {}

/**
 * The router builds its addresses for a language and rebuilds them when it changes, so it is the
 * one who is told: it registers here instead of being imported, which would be a circle.
 */
export function onApply(handler)
{
  onChange = handler
}

/** Language applied on the client: i18n + store (persisted). Nothing is sent to the server. */
function applyLocal(language)
{
  const value = isSupported(language) ? language : defaultLanguage()

  i18n.global.locale.value = value
  const store = useMainStore()
  store.setProperty('language', value, 'settings')

  return value
}

/**
 * Called once at boot: the language of the app, which is the one its address carries or, when the address
 * carries none, the visitor's own one — that address is moved there right away (see settleAddress in
 * router/index.js). The choice on the device is read before any language is written: applying one stores
 * it, so reading it afterwards would only find the address's.
 */
export function start()
{
  device = askDevice()

  return applyLocal(fromAddress() ?? device)
}

/** A language chosen while the app runs: the texts change here, the addresses in the router. */
export function apply(language)
{
  const before = i18n.global.locale.value
  const value = applyLocal(language)

  if (value !== before) {
    onChange(value)
  }

  return value
}

/** Logged-in user: save the preferred language on the server and apply it on the client. */
export function save(language)
{
  apply(language)
  const store = useMainStore()

  return Server.put(store.apiPath.user_settings, { language })
    .then(response => apply(response.data?.data?.language ?? language))
}

/** Logged-in user: load the preferred language from the server and apply it on the client. */
export function syncFromServer()
{
  const store = useMainStore()

  return Server.get(store.apiPath.user_settings)
    .then(response => apply(response.data?.data?.language ?? current()))
}
